import { useState, useCallback, useRef, useEffect } from 'react';
import {
  APIProvider,
  Map,
  AdvancedMarker,
  Pin,
  useMap
} from '@vis.gl/react-google-maps';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import {
  MapPin,
  Plus,
  Trash2,
  Search,
  GripVertical,
  Loader2
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

// Default center (London)
const DEFAULT_CENTER = { lat: 51.5074, lng: -0.1278 };

// Component to handle Places Search with clickable results
function PlacesSearch({ onPlaceSelect }) {
  const map = useMap();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchTimeoutRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);

  useEffect(() => {
    if (!window.google?.maps?.places) return;

    autocompleteServiceRef.current =
      new window.google.maps.places.AutocompleteService();
    if (map) {
      placesServiceRef.current = new window.google.maps.places.PlacesService(
        map
      );
    }
  }, [map]);

  const handleSearch = useCallback(query => {
    setSearchQuery(query);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!query.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(() => {
      if (!autocompleteServiceRef.current) return;

      autocompleteServiceRef.current.getPlacePredictions(
        {
          input: query,
          componentRestrictions: { country: 'uk' }
        },
        (predictions, status) => {
          setIsSearching(false);
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            setSearchResults(predictions);
            setShowResults(true);
          } else {
            setSearchResults([]);
            setShowResults(false);
          }
        }
      );
    }, 300);
  }, []);

  const handleSelectPlace = useCallback(
    (placeId, description) => {
      if (!placesServiceRef.current) return;

      placesServiceRef.current.getDetails(
        { placeId, fields: ['formatted_address', 'geometry', 'name'] },
        (place, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            place
          ) {
            const location = {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng()
            };

            const address =
              place.formatted_address || place.name || description;
            onPlaceSelect(address, location);

            // Pan map to location
            map.panTo(location);
            map.setZoom(16);

            // Clear search
            setSearchQuery('');
            setSearchResults([]);
            setShowResults(false);
          }
        }
      );
    },
    [map, onPlaceSelect]
  );

  return (
    <div className='relative'>
      <div className='relative'>
        <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none z-10' />
        <Input
          type='text'
          placeholder='Search for an address...'
          className='pl-10'
          value={searchQuery}
          onChange={e => handleSearch(e.target.value)}
          onFocus={() => searchResults.length > 0 && setShowResults(true)}
        />
        {isSearching && (
          <Loader2 className='absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground' />
        )}
      </div>

      {/* Search Results Dropdown */}
      {showResults && searchResults.length > 0 && (
        <Card className='absolute w-full z-50 shadow-lg border-2 py-0'>
          <ScrollArea className='max-h-60 overflow-auto'>
            <div className='p-2'>
              {searchResults.map(result => (
                <button
                  key={result.place_id}
                  type='button'
                  onClick={() =>
                    handleSelectPlace(result.place_id, result.description)
                  }
                  className='w-full text-left p-3 hover:bg-gray-100 rounded-lg transition-colors flex items-start gap-2'
                >
                  <MapPin className='h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0' />
                  <div className='flex-1 min-w-0'>
                    <p className='text-sm font-medium truncate'>
                      {result.structured_formatting.main_text}
                    </p>
                    <p className='text-xs text-muted-foreground truncate'>
                      {result.structured_formatting.secondary_text}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </ScrollArea>
          <div className='border-t p-2'>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='w-full'
              onClick={() => setShowResults(false)}
            >
              Close
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

// Draggable marker component
function DraggableMarker({
  position,
  onDragEnd,
  label,
  onDelete,
  isMain = false,
  color = '#EA4335'
}) {
  const [markerRef, setMarkerRef] = useState(null);

  const handleDragEnd = useCallback(
    event => {
      if (!event.latLng) return;

      const newPosition = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      };

      onDragEnd(newPosition);
    },
    [onDragEnd]
  );

  return (
    <AdvancedMarker
      ref={setMarkerRef}
      position={position}
      draggable={true}
      onDragEnd={handleDragEnd}
      title={label}
    >
      <div className='relative flex flex-col items-center'>
        <Pin background={color} borderColor='#fff' glyphColor='#fff' />
        {label && (
          <div className='absolute -top-10 bg-white px-2 py-1 rounded shadow-lg border text-xs font-semibold whitespace-nowrap flex items-center gap-1'>
            <GripVertical className='h-3 w-3 text-gray-400' />
            {label}
            {!isMain && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  onDelete?.();
                }}
                className='ml-1 text-red-500 hover:text-red-700'
              >
                <Trash2 className='h-3 w-3' />
              </button>
            )}
          </div>
        )}
      </div>
    </AdvancedMarker>
  );
}

export function LocationMapPicker({ value, onChange, className = '' }) {
  const [mapCenter, setMapCenter] = useState(
    value?.mainCoordinates || DEFAULT_CENTER
  );
  const [mainAddress, setMainAddress] = useState(value?.mainAddress || '');
  const [mainCoordinates, setMainCoordinates] = useState(
    value?.mainCoordinates || null
  );
  const [pins, setPins] = useState(value?.pins || []);
  const [newPinLabel, setNewPinLabel] = useState('');
  const [isAddingPin, setIsAddingPin] = useState(false);
  const [pendingPinPosition, setPendingPinPosition] = useState(null);

  // Sync internal state when value prop changes (e.g., when loading saved data)
  useEffect(() => {
    if (value) {
      if (value.mainAddress) setMainAddress(value.mainAddress);
      if (value.mainCoordinates) {
        setMainCoordinates(value.mainCoordinates);
        setMapCenter(value.mainCoordinates);
      }
      if (value.pins) setPins(value.pins);
    }
  }, [value]);

  // Helper to notify parent of changes
  const notifyChange = useCallback(
    (address, coordinates, pinsList) => {
      if (coordinates || pinsList.length > 0) {
        onChange({
          mainAddress: address,
          mainCoordinates: coordinates,
          pins: pinsList
        });
      }
    },
    [onChange]
  );

  const handlePlaceSelect = useCallback(
    (address, coordinates) => {
      setMainAddress(address);
      setMainCoordinates(coordinates);
      setMapCenter(coordinates);
      // Notify parent immediately
      notifyChange(address, coordinates, pins);
    },
    [pins, notifyChange]
  );

  const handleMainMarkerDragEnd = useCallback(
    newPosition => {
      setMainCoordinates(newPosition);
      // Notify parent immediately
      notifyChange(mainAddress, newPosition, pins);
    },
    [mainAddress, pins, notifyChange]
  );

  const handleAddPin = useCallback(() => {
    if (!newPinLabel.trim()) return;

    // Add pin at center of current map view
    const newPin = {
      id: crypto.randomUUID(),
      label: newPinLabel.trim(),
      coordinates: pendingPinPosition || mapCenter
    };

    const updatedPins = [...pins, newPin];
    setPins(updatedPins);
    setNewPinLabel('');
    setIsAddingPin(false);
    setPendingPinPosition(null);
    // Notify parent immediately
    notifyChange(mainAddress, mainCoordinates, updatedPins);
  }, [
    newPinLabel,
    mapCenter,
    pendingPinPosition,
    pins,
    mainAddress,
    mainCoordinates,
    notifyChange
  ]);

  const handlePinDragEnd = useCallback(
    (pinId, newPosition) => {
      const updatedPins = pins.map(pin =>
        pin.id === pinId ? { ...pin, coordinates: newPosition } : pin
      );
      setPins(updatedPins);
      // Notify parent immediately
      notifyChange(mainAddress, mainCoordinates, updatedPins);
    },
    [pins, mainAddress, mainCoordinates, notifyChange]
  );

  const handleDeletePin = useCallback(
    pinId => {
      const updatedPins = pins.filter(pin => pin.id !== pinId);
      setPins(updatedPins);
      // Notify parent immediately
      notifyChange(mainAddress, mainCoordinates, updatedPins);
    },
    [pins, mainAddress, mainCoordinates, notifyChange]
  );

  const handleMapClick = useCallback(
    event => {
      if (!isAddingPin || !event.detail?.latLng) return;

      const clickedPosition = {
        lat: event.detail.latLng.lat,
        lng: event.detail.latLng.lng
      };

      setPendingPinPosition(clickedPosition);
    },
    [isAddingPin]
  );

  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <Card className='p-6'>
        <div className='text-center space-y-2'>
          <MapPin className='h-12 w-12 mx-auto text-muted-foreground' />
          <p className='text-sm text-muted-foreground'>
            Google Maps API key is not configured. Please add
            VITE_GOOGLE_MAPS_API_KEY to your .env file.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={['places']}>
        {/* Address Search */}
        <div className='space-y-2'>
          <Label>Search for Location</Label>
          <PlacesSearch onPlaceSelect={handlePlaceSelect} />
          {mainAddress && (
            <p className='text-sm text-muted-foreground flex items-center gap-2'>
              <MapPin className='h-4 w-4' />
              {mainAddress}
            </p>
          )}
        </div>

        {/* Map */}
        <Card className='overflow-hidden'>
          <div className='h-[400px] w-full'>
            <Map
              mapId='location-picker-map'
              defaultCenter={mapCenter}
              defaultZoom={mainCoordinates ? 16 : 12}
              gestureHandling='cooperative'
              disableDefaultUI={false}
              onClick={handleMapClick}
              clickableIcons={false}
            >
              {/* Main location marker */}
              {mainCoordinates && (
                <DraggableMarker
                  position={mainCoordinates}
                  onDragEnd={handleMainMarkerDragEnd}
                  label='Main Location'
                  isMain={true}
                  color='#4285F4'
                />
              )}

              {/* Additional pins */}
              {pins.map(pin => (
                <DraggableMarker
                  key={pin.id}
                  position={pin.coordinates}
                  onDragEnd={newPos => handlePinDragEnd(pin.id, newPos)}
                  label={pin.label}
                  onDelete={() => handleDeletePin(pin.id)}
                  color='#EA4335'
                />
              ))}

              {/* Pending pin preview */}
              {isAddingPin && pendingPinPosition && (
                <AdvancedMarker position={pendingPinPosition}>
                  <Pin
                    background='#FCD34D'
                    borderColor='#fff'
                    glyphColor='#fff'
                  />
                </AdvancedMarker>
              )}
            </Map>
          </div>
        </Card>

        {/* Pin Management */}
        <div className='space-y-3'>
          <div className='flex items-center justify-between'>
            <Label>Additional Pins</Label>
            {!isAddingPin && (
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={() => setIsAddingPin(true)}
                disabled={!mainCoordinates}
              >
                <Plus className='h-4 w-4 mr-1' />
                Add Pin
              </Button>
            )}
          </div>

          {/* Existing pins */}
          {pins.length > 0 && (
            <div className='space-y-2'>
              {pins.map(pin => (
                <div
                  key={pin.id}
                  className='flex items-center justify-between p-2 bg-gray-50 rounded border'
                >
                  <div className='flex items-center gap-2'>
                    <MapPin className='h-4 w-4 text-red-500' />
                    <span className='text-sm font-medium'>{pin.label}</span>
                  </div>
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    onClick={() => handleDeletePin(pin.id)}
                    className='h-8 w-8 p-0 text-red-600 hover:text-red-700'
                  >
                    <Trash2 className='h-4 w-4' />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Add new pin form */}
          {isAddingPin && (
            <Card className='p-4 bg-blue-50 border-blue-200'>
              <div className='space-y-3'>
                <div>
                  <Label htmlFor='pin-label' className='text-sm'>
                    Pin Label
                  </Label>
                  <Input
                    id='pin-label'
                    value={newPinLabel}
                    onChange={e => setNewPinLabel(e.target.value)}
                    placeholder='e.g., Site Entrance, Parking, Office'
                    className='mt-1'
                    autoFocus
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddPin();
                      } else if (e.key === 'Escape') {
                        setIsAddingPin(false);
                        setNewPinLabel('');
                        setPendingPinPosition(null);
                      }
                    }}
                  />
                </div>
                <div className='text-xs text-muted-foreground'>
                  {pendingPinPosition
                    ? 'Click "Add Pin" to confirm, or click another location on the map to adjust'
                    : 'Click on the map to place the pin, then add a label'}
                </div>
                <div className='flex gap-2'>
                  <Button
                    type='button'
                    size='sm'
                    onClick={handleAddPin}
                    disabled={!newPinLabel.trim() || !pendingPinPosition}
                    className='bg-green-600 hover:bg-green-700'
                  >
                    <Plus className='h-4 w-4 mr-1' />
                    Add Pin
                  </Button>
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    onClick={() => {
                      setIsAddingPin(false);
                      setNewPinLabel('');
                      setPendingPinPosition(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {!mainCoordinates && !isAddingPin && (
            <p className='text-xs text-muted-foreground italic'>
              Search for and select a main location first before adding
              additional pins
            </p>
          )}
        </div>
      </APIProvider>
    </div>
  );
}
